import React, { useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import {
    Search, ShoppingBag, Heart, Menu, X, User, PackageSearch, LogOut, Sparkles, LayoutDashboard,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "@/contexts/AuthContext";
import { useCart } from "@/contexts/CartContext";
import { useSettings } from "@/contexts/SettingsContext";
import ThemeToggle from "@/components/ThemeToggle";
import { Button } from "@/components/ui/button";

const NAV_LINKS = [
    { to: "/", label: "Home" },
    { to: "/shop", label: "Shop" },
    { to: "/how-it-works", label: "How It Works" },
    { to: "/gallery", label: "Gallery" },
    { to: "/reviews", label: "Reviews" },
    { to: "/faqs", label: "FAQs" },
    { to: "/track", label: "Track Order" },
    { to: "/founders", label: "Founders" },
    { to: "/contact", label: "Contact" },
];

const Navigation = () => {
    const { user, logout } = useAuth();
    const { count } = useCart();
    const { settings } = useSettings();
    const location = useLocation();
    const navigate = useNavigate();
    const [open, setOpen] = useState(false);
    const [searchOpen, setSearchOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const [profileOpen, setProfileOpen] = useState(false);
    const profileRef = useRef();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 20);
        window.addEventListener("scroll", onScroll);
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => {
        setOpen(false);
        setProfileOpen(false);
    }, [location.pathname]);

    useEffect(() => {
        const onClick = (e) => {
            if (profileRef.current && !profileRef.current.contains(e.target)) setProfileOpen(false);
        };
        document.addEventListener("mousedown", onClick);
        return () => document.removeEventListener("mousedown", onClick);
    }, []);

    const doSearch = (e) => {
        e.preventDefault();
        const q = new FormData(e.currentTarget).get("q");
        if (q) navigate(`/shop?q=${encodeURIComponent(q)}`);
        setSearchOpen(false);
    };

    return (
        <header className="sticky top-0 z-40 w-full flex justify-center pointer-events-none">
            <div className={`pointer-events-auto mt-4 w-[min(1200px,94%)] transition-all ${scrolled ? "glass-strong" : "glass"} rounded-full px-4 py-2 md:px-6 md:py-2.5 flex items-center gap-3`} data-testid="main-navigation">
                <Link to="/" className="flex items-center gap-2 pr-3 group" data-testid="nav-logo">
                    <div className="w-10 h-10 rounded-full overflow-hidden ring-2 ring-brand-gold/40 shadow-md bg-white">
                        <img
                            src={settings?.logo_url || "https://customer-assets-cm19k8pv.emergentagent.net/job_custom-collectibles-2/artifacts/vm51spgg_Screenshot%202026-07-23%20at%206.35.36%E2%80%AFAM.png"}
                            alt="Cherishables"
                            className="w-full h-full object-cover"
                        />
                    </div>
                    <span className="font-display text-lg md:text-xl font-bold text-primary hidden sm:inline">Cherishables</span>
                </Link>

                <nav className="hidden lg:flex items-center gap-1 flex-1">
                    {NAV_LINKS.map((l) => (
                        <NavLink
                            key={l.to}
                            to={l.to}
                            end={l.to === "/"}
                            data-testid={`nav-link-${l.label.replace(/\s+/g, "-").toLowerCase()}`}
                            className={({ isActive }) =>
                                `px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                                    isActive
                                        ? "bg-primary/10 text-primary"
                                        : "text-foreground/70 hover:text-primary hover:bg-primary/5"
                                }`
                            }
                        >
                            {l.label}
                        </NavLink>
                    ))}
                </nav>

                <div className="flex items-center gap-1 ml-auto lg:ml-0">
                    <button
                        data-testid="nav-search-btn"
                        onClick={() => setSearchOpen((s) => !s)}
                        className="w-9 h-9 rounded-full flex items-center justify-center hover:bg-primary/5"
                        aria-label="Search"
                    >
                        <Search className="w-4 h-4" />
                    </button>
                    <Link to="/dashboard/wishlist" data-testid="nav-wishlist" className="w-9 h-9 rounded-full flex items-center justify-center hover:bg-primary/5 hidden sm:flex">
                        <Heart className="w-4 h-4" />
                    </Link>
                    <Link to="/checkout" data-testid="nav-cart" className="relative w-9 h-9 rounded-full flex items-center justify-center hover:bg-primary/5">
                        <ShoppingBag className="w-4 h-4" />
                        {count > 0 && (
                            <span className="absolute -top-1 -right-1 bg-primary text-primary-foreground text-[10px] rounded-full min-w-[18px] h-[18px] px-1 flex items-center justify-center font-bold" data-testid="cart-count">
                                {count}
                            </span>
                        )}
                    </Link>
                    <div className="hidden md:block"><ThemeToggle /></div>
                    {user ? (
                        <div className="relative" ref={profileRef}>
                            <button
                                data-testid="nav-profile-btn"
                                onClick={() => setProfileOpen((s) => !s)}
                                className="ml-1 flex items-center gap-2 pl-1 pr-3 py-1 rounded-full bg-primary/10 hover:bg-primary/20 transition-colors"
                            >
                                {user.avatar_url ? (
                                    <img src={user.avatar_url} alt="me" className="w-7 h-7 rounded-full object-cover" />
                                ) : (
                                    <div className="w-7 h-7 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xs font-bold">
                                        {(user.name || user.email || "U").slice(0, 1).toUpperCase()}
                                    </div>
                                )}
                                <span className="text-sm font-medium hidden md:inline max-w-[80px] truncate">{user.name?.split(" ")[0]}</span>
                            </button>
                            <AnimatePresence>
                                {profileOpen && (
                                    <motion.div
                                        initial={{ opacity: 0, y: -6 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{ opacity: 0, y: -6 }}
                                        className="absolute right-0 mt-2 w-56 rounded-2xl glass-strong p-2 shadow-xl"
                                        data-testid="profile-menu"
                                    >
                                        <div className="px-3 py-2 border-b border-border/50">
                                            <div className="text-sm font-semibold truncate">{user.name}</div>
                                            <div className="text-xs text-muted-foreground truncate">{user.email}</div>
                                        </div>
                                        <Link to="/dashboard" className="flex items-center gap-2 px-3 py-2 rounded-xl hover:bg-primary/5 text-sm" data-testid="profile-menu-dashboard">
                                            <LayoutDashboard className="w-4 h-4" /> Dashboard
                                        </Link>
                                        <Link to="/dashboard/orders" className="flex items-center gap-2 px-3 py-2 rounded-xl hover:bg-primary/5 text-sm">
                                            <PackageSearch className="w-4 h-4" /> Orders
                                        </Link>
                        {(user.role === "admin" || user.role === "super_admin") && (
                                            <Link to="/admin" className="flex items-center gap-2 px-3 py-2 rounded-xl hover:bg-primary/5 text-sm text-primary" data-testid="profile-menu-admin">
                                                <Sparkles className="w-4 h-4" /> Admin Portal
                                            </Link>
                                        )}
                                        {["sales", "support", "designer", "production", "painting", "packing", "finance", "operations"].includes(user.role) && (
                                            <Link to="/team" className="flex items-center gap-2 px-3 py-2 rounded-xl hover:bg-primary/5 text-sm text-primary" data-testid="profile-menu-team">
                                                <Sparkles className="w-4 h-4" /> Team Portal
                                            </Link>
                                        )}
                                        <button onClick={logout} className="w-full flex items-center gap-2 px-3 py-2 rounded-xl hover:bg-destructive/10 text-sm text-destructive" data-testid="profile-menu-logout">
                                            <LogOut className="w-4 h-4" /> Log out
                                        </button>
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </div>
                    ) : (
                        null
                    )}
                    <button
                        data-testid="nav-menu-btn"
                        onClick={() => setOpen((s) => !s)}
                        className="lg:hidden w-9 h-9 rounded-full flex items-center justify-center hover:bg-primary/5"
                        aria-label="Menu"
                    >
                        {open ? <X className="w-4 h-4" /> : <Menu className="w-4 h-4" />}
                    </button>
                </div>
            </div>

            {/* Search bar */}
            <AnimatePresence>
                {searchOpen && (
                    <motion.form
                        initial={{ opacity: 0, y: -8 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -8 }}
                        onSubmit={doSearch}
                        className="absolute top-full mt-3 pointer-events-auto w-[min(600px,90%)] rounded-full glass-strong px-4 py-2 flex items-center gap-2"
                    >
                        <Search className="w-4 h-4 text-muted-foreground" />
                        <input
                            data-testid="nav-search-input"
                            autoFocus
                            name="q"
                            placeholder="Search miniatures, wedding, pets, corporate…"
                            className="flex-1 bg-transparent outline-none text-sm"
                        />
                        <button className="text-xs font-medium text-primary" type="submit">Search</button>
                    </motion.form>
                )}
            </AnimatePresence>

            {/* Mobile menu */}
            <AnimatePresence>
                {open && (
                    <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        className="absolute top-full mt-3 pointer-events-auto w-[min(500px,94%)] rounded-3xl glass-strong p-3 lg:hidden"
                        data-testid="mobile-menu"
                    >
                        <div className="flex flex-col gap-1">
                            {NAV_LINKS.map((l) => (
                                <NavLink key={l.to} to={l.to} end={l.to === "/"}
                                    className={({ isActive }) =>
                                        `px-4 py-2 rounded-xl text-sm ${isActive ? "bg-primary/10 text-primary" : "text-foreground/80 hover:bg-primary/5"}`
                                    }
                                >{l.label}</NavLink>
                            ))}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </header>
    );
};

export default Navigation;
